import { AudioSfx } from '../../../learn/constants/sfx.ts';
import { mapPartToSpeakableScriptPart } from '../../../learn/mappers/part-to-speakable-script-part.mapper.ts';
import { mapSentenceToSpeakableScriptPart } from '../../../learn/mappers/sentence-to-speakable-script-part.mapper.ts';
import { selectCurrentLessonCharacter } from '../../../learn/selectors/current-lesson-character.selector.ts';
import { selectCurrentLessonTaskStep } from '../../../learn/selectors/current-lesson-task-step.selector.ts';
import { selectCurrentSentence } from '../../../learn/selectors/current-sentence.selector.ts';
import { selectCurrentStepParts } from '../../../learn/selectors/current-step-parts.ts';
import { selectPrepareScriptToSpeakProps } from '../../../learn/selectors/prepare-script-to-speak-props.selector.ts';
import { setIsSpeaking } from '../../../learn/slices/app-state/app-state.slice.ts';

import { Character } from '../../types/asset/character.types.ts';
import { SpeakableScriptPart, SpeakableScriptPartType } from '../../types/asset/script.types.ts';
import { AudioPlayerErrorType } from '../../types/audio.types.ts';
import { PromptOptions } from '../../types/prompt-options.types.ts';

import { getLessonsUtil } from '../asset/lessons.util.ts';
import { getScriptsUtil } from '../asset/scripts.util.ts';
import { getSentryUtil } from '../monitoring/sentry.util.ts';
import { getReduxStoreUtil } from '../redux-store.util.ts';

import { getAudioContextUtil } from './audio-context.util.ts';
import { AudioMediaSourceUtil } from './audio-media-source.util.ts';
import { getAudioLettersProviderUtil } from './audio-phoneme-provider.util.ts';
import { getAudioSfxProviderUtil } from './audio-sfx-provider.util.ts';
import { getGenerateSpeechUtil } from './generate-speech.util.ts';
import { getTextToSpeechUtil } from './text-to-speech.util.ts';

enum AudioQueueItemType {
  SFX = 'sfx',
  LETTER = 'letter',
  MEDIA_SOURCE = 'media_source',
}

type AudioQueueItem =
  | {
      type: AudioQueueItemType.SFX;
      sfx: AudioSfx;
    }
  | {
      type: AudioQueueItemType.LETTER;
      letter: string;
    }
  | {
      type: AudioQueueItemType.MEDIA_SOURCE;
      audioMediaSource: AudioMediaSourceUtil;
      text?: string;
      volume?: number;
    };

export class AudioPlayerUtil {
  private static _instance: AudioPlayerUtil;
  private queue: AudioQueueItem[] = [];
  private currentAudioMediaSource: AudioMediaSourceUtil | null = null;
  private currentAudio: HTMLAudioElement | null = null;
  private isProcessing = false;
  private completionListeners: (() => void | Promise<void>)[] = [];
  private firstQueuedPartPlayedListeners: (() => void | Promise<void>)[] = [];
  private hasFirstPartPlayed = false;
  private playbackStoppedListeners: (() => void | Promise<void>)[] = [];
  private hasPlaybackStopped = false;

  private constructor(
    private readonly audioContextUtil = getAudioContextUtil(),
    private readonly generateSpeechUtil = getGenerateSpeechUtil(),
    private readonly lessonsUtil = getLessonsUtil(),
    private readonly lettersProvider = getAudioLettersProviderUtil(),
    private readonly reduxStoreUtil = getReduxStoreUtil(),
    private readonly scriptsUtil = getScriptsUtil(),
    private readonly sfxProvider = getAudioSfxProviderUtil(),
    private readonly textToSpeechUtil = getTextToSpeechUtil()
  ) {}

  static get instance(): AudioPlayerUtil {
    if (!AudioPlayerUtil._instance) {
      AudioPlayerUtil._instance = new AudioPlayerUtil();
    }
    return AudioPlayerUtil._instance;
  }

  private splitTextIntoSentences(text: string): string[] {
    return text
      .replace(/([.?!])\s*(?=[A-Z])/g, '$1|')
      .split('|')
      .map((sentence) => sentence.trim())
      .filter(Boolean);
  }

  async speakStep(): Promise<void> {
    this.stopPlaying(); // This should prevent re-speaking the same step

    const state = await this.reduxStoreUtil.getRootState();
    const props = selectPrepareScriptToSpeakProps(state);
    const character = selectCurrentLessonCharacter(state);

    const speakableParts = this.scriptsUtil.prepareScriptToSpeak(props);

    for (const part of speakableParts) {
      this.queueSpeakableScriptPart(part, character);
    }
    this.processQueue();
  }

  async speakStepParts(): Promise<void> {
    const state = await this.reduxStoreUtil.getRootState();
    const stepParts = selectCurrentStepParts(state);
    if (!stepParts) {
      return;
    }

    const character = selectCurrentLessonCharacter(state);
    for (const part of stepParts) {
      const speakablePart = mapPartToSpeakableScriptPart(part);
      if (speakablePart) {
        this.queueSpeakableScriptPart(speakablePart, character);
      }
    }
    this.processQueue();
  }

  async speakPronunciation(): Promise<void> {
    const state = await this.reduxStoreUtil.getRootState();
    const stepParts = selectCurrentStepParts(state);
    if (stepParts) {
      await this.speakStepParts();
      this.processQueue();
      return;
    }

    const { task, step } = selectCurrentLessonTaskStep(state);
    const pronunciationKey = this.lessonsUtil.getPronunciationKey(task, step);
    if (pronunciationKey) {
      const character = selectCurrentLessonCharacter(state);
      const speakablePart = mapPartToSpeakableScriptPart(pronunciationKey);
      if (speakablePart) {
        this.queueSpeakableScriptPart(speakablePart, character);
        this.processQueue();
        return;
      }
    }

    const currentSentence = selectCurrentSentence(state);
    if (currentSentence.length > 0) {
      const character = selectCurrentLessonCharacter(state);
      const speakablePart = mapSentenceToSpeakableScriptPart(currentSentence);
      this.queueSpeakableScriptPart(speakablePart, character);
      this.processQueue();
      return;
    }
  }

  async getStepPronunciationDuration(): Promise<number> {
    const buffer = 0.25;
    const state = await this.reduxStoreUtil.getRootState();
    const stepParts = selectCurrentStepParts(state);
    const { task, step } = selectCurrentLessonTaskStep(state);
    if (!stepParts) {
      const pronunciationKey = this.lessonsUtil.getPronunciationKey(task, step);
      if (!pronunciationKey) {
        return 0;
      }
      const speakablePart = mapPartToSpeakableScriptPart(pronunciationKey);
      if (speakablePart && speakablePart.type === SpeakableScriptPartType.Letter) {
        return buffer + (await this.lettersProvider.getLetterDuration(speakablePart.letter));
      }
      return 0;
    }

    const durations = await Promise.all(
      stepParts.map(async (part) => {
        const speakablePart = mapPartToSpeakableScriptPart(part);
        if (speakablePart && speakablePart.type === SpeakableScriptPartType.Letter) {
          return await this.lettersProvider.getLetterDuration(speakablePart.letter);
        }
        return 0;
      })
    );
    return durations.reduce((acc, duration) => acc + duration, buffer);
  }

  speak(text: string, character: Character): void {
    this.queueText(text, character);
    this.processQueue();
  }

  generateAndSpeak(promptOptions: PromptOptions, character: Character, errorHandlingMessage?: string): void {
    const audioMediaSource = this.generateSpeechUtil.createAudioMediaSource(
      {
        promptOptions,
        character: character.characterType ?? '',
        errorHandlingMessage: errorHandlingMessage ?? '',
      },
      this.onAudioPlay.bind(this)
    );
    this.queueMediaSource(audioMediaSource, promptOptions.promptType, character.volume);
    this.processQueue();
  }

  private queueSpeakableScriptPart(speakableScriptPart: SpeakableScriptPart, character: Character): void {
    if (speakableScriptPart.type === SpeakableScriptPartType.Text) {
      this.queueText(speakableScriptPart.text, character);
    } else if (speakableScriptPart.type === SpeakableScriptPartType.Letter) {
      this.queueLetter(speakableScriptPart.letter);
    }
  }

  private queueText(text: string, character: Character): void {
    const sentences = this.splitTextIntoSentences(text);

    for (const sentence of sentences) {
      const audioMediaSource = this.textToSpeechUtil.createAudioMediaSource(
        {
          text: sentence,
          character: character.characterType ?? '',
        },
        this.onAudioPlay.bind(this)
      );
      this.queueMediaSource(audioMediaSource, sentence, character.volume);
    }
  }

  private queueMediaSource(audioMediaSource: AudioMediaSourceUtil, text?: string, volume?: number): void {
    this.queue.push({ type: AudioQueueItemType.MEDIA_SOURCE, audioMediaSource, text, volume: volume ?? 1 });
  }

  private queueLetter(letter: string): void {
    this.queue.push({ type: AudioQueueItemType.LETTER, letter });
  }

  private async processQueue(): Promise<void> {
    if (this.isProcessing) return;
    this.isProcessing = true;
    this.hasFirstPartPlayed = false;
    this.hasPlaybackStopped = false;

    const dispatch = await this.reduxStoreUtil.getDispatch();
    dispatch(setIsSpeaking(true));

    try {
      while (this.queue.length > 0) {
        const item = this.queue.shift();
        if (!item) break;

        if (item.type === AudioQueueItemType.MEDIA_SOURCE) {
          console.log('Speaking', item.text);
          await this.playAudioMediaSource(item.audioMediaSource, item.volume);
        } else if (item.type === AudioQueueItemType.SFX) {
          console.log('Playing', item.sfx);
          await this.playSoundEffect(item.sfx);
        } else if (item.type === AudioQueueItemType.LETTER) {
          console.log('Playing', item.letter);
          await this.playLetter(item.letter);
        }
      }
    } finally {
      this.isProcessing = false;
      dispatch(setIsSpeaking(false));
      await this.notifyCompletionListeners();
    }
  }

  addFirstQueuedPartPlayedListener(listener: () => void): void {
    if (this.hasFirstPartPlayed && this.isProcessing) {
      listener();
    } else {
      this.firstQueuedPartPlayedListeners.push(listener);
    }
  }

  private async notifyFirstQueuedPartPlayedListeners(): Promise<void> {
    await Promise.allSettled(this.firstQueuedPartPlayedListeners.map((listener) => listener()));
    this.firstQueuedPartPlayedListeners = [];
  }

  async waitStartSpeaking(): Promise<void> {
    return new Promise((resolve) => {
      this.addFirstQueuedPartPlayedListener(resolve);
    });
  }

  stopPlaying(): void {
    this.audioContextUtil.stopPlaying();
    this.currentAudioMediaSource?.stop();
    this.currentAudioMediaSource = null;
    this.queue = [];
    this.hasFirstPartPlayed = false;
    this.firstQueuedPartPlayedListeners = [];
    this.hasPlaybackStopped = true;
    this.notifyPlaybackStoppedListeners().catch(() => undefined);
    if (this.currentAudio) {
      this.currentAudio.muted = true;
      this.currentAudio = null;
    }
  }

  addSpeakCompletionListener(listener: () => void): void {
    if (!this.isProcessing) {
      listener();
    } else {
      this.completionListeners.push(listener);
    }
  }

  private async notifyCompletionListeners(): Promise<void> {
    await Promise.allSettled(this.completionListeners.map((listener) => listener()));
    this.completionListeners = [];
  }

  addPlaybackStoppedListener(listener: () => void): void {
    if (this.hasPlaybackStopped) {
      listener();
    } else {
      this.playbackStoppedListeners.push(listener);
    }
  }

  private async notifyPlaybackStoppedListeners(): Promise<void> {
    await Promise.allSettled(this.playbackStoppedListeners.map((listener) => listener()));
    this.playbackStoppedListeners = [];
  }

  async waitForPlaybackStopped(): Promise<void> {
    return new Promise((resolve) => {
      this.addPlaybackStoppedListener(resolve);
    });
  }

  async waitSpeakCompletion(): Promise<void> {
    return new Promise((resolve) => {
      this.addSpeakCompletionListener(resolve);
    });
  }

  async playSoundEffect(soundEffect: AudioSfx): Promise<void> {
    const sfxStream = this.sfxProvider.getSfx(soundEffect);
    if (!sfxStream) {
      this.handleError(new Error('SFX not found'), AudioPlayerErrorType.SFX_NOT_FOUND);
      return;
    }
    await this.playAudioFile(sfxStream, false);
  }

  async playLetter(letter: string): Promise<void> {
    const letterAudio = this.lettersProvider.getLetter(letter);
    if (!letterAudio) {
      this.handleError(new Error('Letter audio not found'), AudioPlayerErrorType.LETTER_NOT_FOUND);
      return;
    }
    await this.playAudioFile(letterAudio);
  }

  private async playAudioFile(audioFile: string, global = true): Promise<void> {
    const audio = new Audio(audioFile);
    if (global) {
      this.currentAudio = audio;
    }
    await audio.play();
    return new Promise<void>((resolve) => {
      audio.onended = (): void => {
        if (global) {
          this.currentAudio = null;
        }
        resolve();
      };
    });
  }

  private onAudioPlay(): void {
    if (!this.hasFirstPartPlayed) {
      this.hasFirstPartPlayed = true;
      this.notifyFirstQueuedPartPlayedListeners();
    }
  }

  private async playAudioMediaSource(audioMediaSource: AudioMediaSourceUtil, volume?: number): Promise<void> {
    this.currentAudioMediaSource = audioMediaSource;
    try {
      await audioMediaSource.play(volume);
    } finally {
      this.currentAudioMediaSource = null;
    }
  }

  private handleError(error: Error, errorType: AudioPlayerErrorType): void {
    getSentryUtil().trackError(error, { errorType });
  }
}

export const getAudioPlayerUtil = (): AudioPlayerUtil => AudioPlayerUtil.instance;
